import { Lock, LockOpen, TextFields } from "@mui/icons-material";
import { Box, Button, Stack, Typography } from "@mui/material";
import type { EditorOptions } from "@tiptap/core";
import { useCallback, useEffect, useRef, useState } from "react";
import {
  LinkBubbleMenu,
  MenuButton,
  RichTextEditor,
  RichTextReadOnly,
  TableBubbleMenu,
  insertImages,
  type RichTextEditorRef,
} from "mui-tiptap";
import EditorMenuControls from "./EditorMenuControls";
import useExtensions from "./useExtensions";

function fileListToImageFiles(fileList: FileList): File[] {
  return Array.from(fileList).filter((file) => {
    const mimeType = (file.type || "").toLowerCase();
    return mimeType.startsWith("image/");
  });
}



interface EditorProps {
  value: string;
  error?: boolean;
  placeholder?: string;
  helperText?: string;
  onChange: (content: string) => void;
  isReadOnly?: boolean;
  noMinimumHeight?: boolean;
  shortToolbar?: boolean;
}

const Editor: React.FC<EditorProps> = ({
  value,
  placeholder = "",
  onChange,
  error = false,
  helperText,
  isReadOnly = false,
  noMinimumHeight = false,
  shortToolbar = false,
}) => {
  const extensions = useExtensions({
    placeholder,
  });
  const rteRef = useRef<RichTextEditorRef>(null);

  const handleNewImageFiles = useCallback(
    (files: File[], insertPosition?: number): void => {
      if (!rteRef.current?.editor) {
        return;
      }

      const attributesForImageFiles = files.map((file) => ({
        src: URL.createObjectURL(file),
        alt: file.name,
      }));

      insertImages({
        images: attributesForImageFiles,
        editor: rteRef.current.editor,
        position: insertPosition,
      });
    },
    [],
  );

  useEffect(() => {
    if (rteRef.current?.editor && rteRef.current.editor.getHTML() !== value) {
      rteRef.current.editor.commands.setContent(value, false);
    }
  }, [value]);

  // const handleDrop: NonNullable<EditorOptions["editorProps"]["handleDrop"]> =
  //   useCallback(
  //     (view, event, _slice, _moved) => {
  //       if (!(event instanceof DragEvent) || !event.dataTransfer) {
  //         return false;
  //       }

  //       const imageFiles = fileListToImageFiles(event.dataTransfer.files);
  //       if (imageFiles.length > 0) {
  //         const insertPosition = view.posAtCoords({
  //           left: event.clientX,
  //           top: event.clientY,
  //         })?.pos;

  //         handleNewImageFiles(imageFiles, insertPosition);

  //         // Return true to treat the event as handled. We call preventDefault
  //         // ourselves for good measure.
  //         event.preventDefault();
  //         return true;
  //       }

  //       return false;
  //     },
  //     [handleNewImageFiles],
  //   );

  // Allow for pasting images
  // const handlePaste: NonNullable<EditorOptions["editorProps"]["handlePaste"]> =
  //   useCallback(
  //     (_view, event, _slice) => {
  //       if (!event.clipboardData) {
  //         return false;
  //       }

  //       const pastedImageFiles = fileListToImageFiles(
  //         event.clipboardData.files,
  //       );
  //       if (pastedImageFiles.length > 0) {
  //         handleNewImageFiles(pastedImageFiles);
  //         // Return true to mark the paste event as handled. This can for
  //         // instance prevent redundant copies of the same image showing up,
  //         // like if you right-click and copy an image from within the editor
  //         // (in which case it will be added to the clipboard both as a file and
  //         // as HTML, which Tiptap would otherwise separately parse.)
  //         return true;
  //       }

  //       // We return false here to allow the standard paste-handler to run.
  //       return false;
  //     },
  //     [handleNewImageFiles],
  //   );

  return (
    <>
      <Box sx={{ '& .ProseMirror': { minHeight: noMinimumHeight ? 0 : 200, width:'100%', padding: 1, overflow: 'auto' }, padding: shortToolbar ? 0 : 2, border: `1px solid ${error ? 'red':'#ccc'}`, borderRadius: 1 }}>
        <RichTextEditor
          ref={rteRef}
          extensions={extensions}
          content={value}
          editable={isReadOnly ? false : true}
          // editorProps={{
          //   handleDrop: handleDrop,
          //   handlePaste: handlePaste
          // }}
          renderControls={() => !isReadOnly ? <EditorMenuControls shortToolbar={shortToolbar} /> : null}
          RichTextFieldProps={{
            variant: 'standard',
          }}
          onUpdate={({editor}) => onChange(editor.getHTML())}
        >
          {() => (
            <>
              <LinkBubbleMenu />
              <TableBubbleMenu />
            </>
          )}
        </RichTextEditor>
      </Box>
      {error && <Typography sx={{fontSize: 13, ml: 1.5}} variant="body2" color="error">{helperText}</Typography>}
    </>
  );
}

export default Editor;